import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  OverlayDrawer,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { PeopleImportView, PeopleRowRecord } from '@lg/core';
import {
  DocumentType,
  FINANCE_DIRECTOR_POSITION_ID,
  ProjectType,
  SHEET_BY_ID,
  type PeopleSheet,
  type TemplateConfig,
} from '@lg/shared';
import { useApi } from '../api/client';
import { DOC_LABEL, errorMessage } from '../ui/format';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  footer: { display: 'flex', gap: tokens.spacingHorizontalS },
  spacer: { flex: 1 },
});

export function RowDrawer({
  view,
  row,
  sheet,
  template,
  editable,
  onClose,
  onSaved,
}: {
  view: PeopleImportView;
  row: PeopleRowRecord | null;
  sheet: PeopleSheet;
  template: TemplateConfig;
  editable: boolean;
  onClose: () => void;
  onSaved: (v: PeopleImportView) => void;
}) {
  const styles = useStyles();
  const api = useApi();
  const def = SHEET_BY_ID[sheet];
  const [data, setData] = useState<Record<string, unknown>>(row?.data ?? {});
  const save = useMutation({
    mutationFn: () => api.savePeopleRow(view.import.id, { rowId: row?.id, sheet, data }),
    onSuccess: onSaved,
  });
  const remove = useMutation({
    mutationFn: () => api.deletePeopleRow(view.import.id, row?.id ?? ''),
    onSuccess: onSaved,
  });

  // Reference lists for dropdown columns.
  const positionIds = view.rows
    .filter((r) => r.sheet === 'positions')
    .map((r) => String(r.data['positionId'] ?? ''));
  const choices: Record<string, string[]> = {
    jobId: template.jobCatalog.map((j) => j.jobId),
    roleId: template.roleCatalog.map((r) => r.roleId),
    documentType: DocumentType.options,
    projectType: ProjectType.options,
    reportsToPositionId: [...new Set([...positionIds, 'REG-FIN-01', FINANCE_DIRECTOR_POSITION_ID])],
  };
  const labelFor = (col: string, v: string): string => {
    if (col === 'jobId') return `${v} · ${template.jobCatalog.find((j) => j.jobId === v)?.title ?? ''}`;
    if (col === 'roleId') return `${v} · ${template.roleCatalog.find((r) => r.roleId === v)?.name ?? ''}`;
    if (col === 'documentType') return DOC_LABEL[v] ?? v;
    if (col === 'reportsToPositionId' && v === 'REG-FIN-01')
      return 'REG-FIN-01 · Regional finance director (D365)';
    if (col === 'reportsToPositionId' && v === FINANCE_DIRECTOR_POSITION_ID)
      return `${FINANCE_DIRECTOR_POSITION_ID} · Finance director (D365)`;
    return v;
  };

  const set = (k: string, v: unknown) => setData((d) => ({ ...d, [k]: v }));
  const issuesFor = (col: string): string | undefined =>
    row?.issues
      .filter((i) => i.column === col || i.column?.split('+').includes(col))
      .map((i) => i.message)
      .join('; ') || undefined;

  return (
    <OverlayDrawer open position="end" size="medium" onOpenChange={(_, s) => !s.open && onClose()}>
      <DrawerHeader>
        <DrawerHeaderTitle>
          {row ? `${def.title} · row ${row.rowNumber}` : `Add ${def.title.toLowerCase()} row`}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={styles.body}>
        {row &&
          row.issues
            .filter((i) => !i.column)
            .map((i, n) => (
              <MessageBar key={n} intent={i.severity === 'error' ? 'error' : 'warning'}>
                <MessageBarBody>
                  {i.rule}: {i.message}
                </MessageBarBody>
              </MessageBar>
            ))}
        {def.columns.map((c) => {
          const value = data[c.key];
          const error = issuesFor(c.key);
          const field = {
            label: c.label,
            required: c.required,
            validationState: error ? ('error' as const) : ('none' as const),
            validationMessage: error,
            hint: `e.g. ${c.example}`,
          };
          if (c.key === 'primary') {
            return (
              <Field key={c.key} {...field}>
                <Switch
                  checked={value !== false && value !== 'no'}
                  disabled={!editable}
                  onChange={(_, d) => set(c.key, d.checked)}
                  label={value === false ? 'No' : 'Yes'}
                />
              </Field>
            );
          }
          const options = choices[c.key];
          if (options) {
            const current = String(value ?? '');
            return (
              <Field key={c.key} {...field}>
                <Dropdown
                  value={current ? labelFor(c.key, current) : ''}
                  selectedOptions={current ? [current] : []}
                  disabled={!editable}
                  placeholder="Select…"
                  onOptionSelect={(_, d) => set(c.key, d.optionValue)}
                >
                  {options.map((o) => (
                    <Option key={o} value={o} text={labelFor(c.key, o)}>
                      {labelFor(c.key, o)}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            );
          }
          return (
            <Field key={c.key} {...field}>
              <Input
                value={value === undefined || value === null ? '' : String(value)}
                disabled={!editable}
                type={c.key.toLowerCase().includes('date') || c.key === 'employmentStart' ? 'date' : 'text'}
                onChange={(_, d) => set(c.key, d.value)}
              />
            </Field>
          );
        })}
        {(save.error ?? remove.error) && (
          <MessageBar intent="error">
            <MessageBarBody>{errorMessage(save.error ?? remove.error)}</MessageBarBody>
          </MessageBar>
        )}
      </DrawerBody>
      <DrawerFooter className={styles.footer}>
        {editable && (
          <Button appearance="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving and re-validating…' : 'Save'}
          </Button>
        )}
        <Button onClick={onClose}>{editable ? 'Cancel' : 'Close'}</Button>
        <span className={styles.spacer} />
        {editable && row && (
          <Button appearance="subtle" disabled={remove.isPending} onClick={() => remove.mutate()}>
            Remove row
          </Button>
        )}
      </DrawerFooter>
    </OverlayDrawer>
  );
}
